<template>
  <Teleport to="body">
    <div v-if="aclaraciones.abierto.value" class="aclaraciones-modal" role="dialog" aria-modal="true" aria-labelledby="aclaraciones-titulo">
      <div class="aclaraciones-modal__backdrop" @click="aclaraciones.cerrar" />

      <div class="aclaraciones-modal__dialog">
        <header class="aclaraciones-modal__header">
          <DsText id="aclaraciones-titulo" as="subtitle4" class="aclaraciones-modal__title">
            Mis Aclaraciones
          </DsText>
          <button type="button" class="aclaraciones-modal__close" aria-label="Cerrar" @click="aclaraciones.cerrar">
            &times;
          </button>
        </header>

        <div class="aclaraciones-modal__body">
          <!-- Paso 1: tipo de aclaración -->
          <section v-if="aclaraciones.mostrarOpcionAclaracion.value" class="aclaraciones-modal__section">
            <DsText as="subtitle7">Selecciona la opción deseada</DsText>

            <div class="aclaraciones-modal__options">
              <button
                v-for="aclaracion in aclaraciones.aclaraciones.value"
                :key="aclaracion.tipoAclaracionId"
                type="button"
                class="aclaraciones-modal__option"
                :class="{ 'is-selected': aclaracion.tipoAclaracionId === aclaraciones.crearAclaracion.value.tipoAclaracionId }"
                @click="aclaraciones.seleccionarAclaracion(aclaracion)"
              >
                {{ aclaracion.nombre }}
              </button>
            </div>

            <p v-if="aclaraciones.avisoAclaracion.value" class="aclaraciones-modal__error">
              Selecciona una opción.
            </p>
          </section>

          <!-- Paso 2: descripción y evidencia -->
          <section v-if="aclaraciones.mostrarDescripcion.value" class="aclaraciones-modal__section">
            <DsText as="subtitle7">Descríbenos brevemente la situación</DsText>

            <textarea
              v-model="descripcion"
              rows="3"
              maxlength="250"
              class="aclaraciones-modal__textarea"
              />
            <p class="aclaraciones-modal__counter">{{ descripcion.length }}/250</p>

            <p v-if="aclaraciones.avisoDescripcion.value" class="aclaraciones-modal__error">Agrega una descripción.</p>
            <p v-if="aclaraciones.mensajeTamanioArchivo.value" class="aclaraciones-modal__error">Tamaño de archivo NO permitido.</p>
            <p v-if="aclaraciones.mensajeTipoArchivo.value" class="aclaraciones-modal__error">Tipo de archivo NO permitido.</p>

            <DsText as="subtitle7">Adjunta documentos de evidencia</DsText>
            <DsText as="body4" class="aclaraciones-modal__hint">
              Puedes adjuntar archivos JPG, PDF, PNG con un peso hasta de 10mb
            </DsText>

            <input
              :id="inputId"
              type="file"
              multiple
              accept=".pdf,.png,.jpg"
              hidden
              @change="aclaraciones.agregarArchivos"
            >
            <label :for="inputId" class="aclaraciones-modal__dropzone">
              <i class="fas fa-paperclip" aria-hidden="true" />
              <span>Arrastra los archivos aquí o <u>búscalos</u></span>
            </label>

            <ul v-if="aclaraciones.files.value.length" class="aclaraciones-modal__files">
              <li v-for="(file, i) in aclaraciones.files.value" :key="`${file.name}-${i}`" class="aclaraciones-modal__file">
                <button
                  type="button"
                  class="aclaraciones-modal__file-remove"
                  aria-label="Eliminar archivo"
                  @click="aclaraciones.eliminarArchivo(i)"
                >
                  <i class="fas fa-trash-alt" aria-hidden="true" />
                </button>
                <a :href="aclaraciones.visualizarArchivo(file)" target="_blank" rel="noreferrer noopener">{{ file.name }}</a>
              </li>
            </ul>
          </section>

          <!-- Paso 3: folio -->
          <section v-if="aclaraciones.mostrarFolio.value" class="aclaraciones-modal__section aclaraciones-modal__folio">
            <DsText as="subtitle4">Aclaraciones</DsText>
            <DsText as="subtitle7">Hemos recibido correctamente tu caso.</DsText>

            <div class="aclaraciones-modal__folio-box">
              <DsText as="body3" color="#ffffff">El folio de tu aclaración es:</DsText>
              <DsText as="subtitle4" color="#ffffff">{{ aclaraciones.folio.value.aclaracionId }}</DsText>
            </div>

            <DsText as="body4">
              En breve uno de nuestros Asesores te contactará para darle seguimiento al caso.
            </DsText>
          </section>
        </div>

        <footer class="aclaraciones-modal__footer">
          <DsButton
            v-if="aclaraciones.mostrarFolio.value"
            label="Cerrar"
            @click="aclaraciones.cerrar"
          />
          <DsButton v-else label="Enviar" @click="aclaraciones.avanzar" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import * as utilidades from '@/utilidades'
import type { useAclaraciones } from './composables/useAclaraciones'
import DsText from '../MaterialApoyo/atoms/DsText.vue'
import DsButton from '../MaterialApoyo/atoms/DsButton.vue'

const props = defineProps<{
  /** Estado y acciones de useAclaraciones(). */
  aclaraciones: ReturnType<typeof useAclaraciones>
}>()

const emit = defineEmits<{
  (e: 'update:descripcion', valor: string): void
}>()

/* Lectura del composable; la escritura se delega al padre vía emit. */
const descripcion = computed({
  get: () => props.aclaraciones.descripcion.value,
  set: valor => emit('update:descripcion', valor)
})

const inputId = computed(() => `aclaraciones-file-${utilidades.generarId()}`)
</script>

<style scoped>
.aclaraciones-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.aclaraciones-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.aclaraciones-modal__dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

.aclaraciones-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
}

.aclaraciones-modal__title {
  color: var(--ds-purple-900);
}

.aclaraciones-modal__close {
  border: none;
  background: transparent;
  color: var(--ds-purple-main);
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
}

.aclaraciones-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
}

.aclaraciones-modal__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.aclaraciones-modal__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aclaraciones-modal__option {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: var(--ds-purple-main);
  color: #ffffff;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.aclaraciones-modal__option.is-selected {
  background: var(--ds-orange-main);
}

.aclaraciones-modal__textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--ds-grey-300);
  border-radius: 8px;
  font-family: inherit;
  resize: vertical;
}

.aclaraciones-modal__counter {
  margin: 0;
  font-size: 12px;
  color: var(--ds-grey-700);
}

.aclaraciones-modal__hint {
  color: var(--ds-grey-700);
}

.aclaraciones-modal__error {
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--ds-error-main);
  color: #ffffff;
  font-size: 14px;
}

.aclaraciones-modal__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px dashed var(--ds-grey-300);
  border-radius: 10px;
  background: #e0e7ea;
  color: var(--ds-grey-700);
  cursor: pointer;
}

.aclaraciones-modal__files {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.aclaraciones-modal__file {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aclaraciones-modal__file-remove {
  border: none;
  background: transparent;
  color: var(--ds-error-main);
  cursor: pointer;
}

.aclaraciones-modal__folio {
  align-items: center;
  text-align: center;
}

.aclaraciones-modal__folio-box {
  width: 100%;
  padding: 20px;
  border-radius: 8px;
  background: var(--ds-purple-main);
}

.aclaraciones-modal__footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 24px;
}

</style>
